import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { addLocaleBundle, translate } from '../util/Translate.util';
import componentLocale from './ErrorBoundary.json';

import './ErrorBoundary.scss';

addLocaleBundle(componentLocale);

const tKey = (element) => 'portal.error-boundary.' + element;

// Resolved without the Text component on purpose: this boundary is the last thing
// standing when the tree below it is broken, so it must not depend on i18n being
// loaded or on any component that could itself throw.
const label = (element, fallback) => translate(tKey(element)) || fallback;

/**
 * Catches render/commit/effect errors so a single broken component degrades into a
 * readable message instead of an empty white page.
 *
 * Without a boundary, React unmounts the *entire* tree on any such error - that is
 * what turned the useEffect cleanup bug into the blank page reported in
 * AZTDSP-12462 / AZTSUR-35898. Only render, lifecycle and effect errors are caught;
 * event handlers and rejected promises never unmount the tree, so they do not
 * need one.
 */
export default class ErrorBoundary extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
  };

  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the stack in the console: the production bundle is minified, so the
    // component stack is often the only usable clue when a user reports this.
    console.error('Unhandled error, rendering fallback instead of a blank page', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className='error-boundary'>
        <h1 className='error-boundary-title'>{label('title', 'Es ist ein Fehler aufgetreten.')}</h1>
        <p className='error-boundary-content'>
          {label('content', 'Bitte laden Sie die Seite neu. Falls das Problem bestehen bleibt, kontaktieren Sie uns.')}
        </p>
        <button className='error-boundary-button' type='button' onClick={this.handleReload}>
          {label('reload', 'Seite neu laden')}
        </button>
      </div>
    );
  }
}
